import { useEffect, useRef, useState } from 'react';
import { observe } from 'react-intersection-observer';

const PREFETCH_MARGIN = '0px 0px 48px 0px';

interface UseLoadMoreSentinelParams {
  scrollRoot: HTMLElement | null;
  canLoadMore: boolean;
  itemCount: number;
  onLoadMore?: () => void;
}

/**
 * An observer only reports when visibility flips. A page that leaves the sentinel in view flips
 * nothing, so the observer is rebuilt whenever the item count changes: a new observer reports the
 * sentinel's current state straight away, which keeps a short list loading until it overflows.
 * Reading an `inView` flag instead would still be stale in the render that appended the page and
 * load one page too many.
 */
export const useLoadMoreSentinel = ({
  scrollRoot,
  canLoadMore,
  itemCount,
  onLoadMore,
}: UseLoadMoreSentinelParams) => {
  const [sentinel, setSentinel] = useState<HTMLElement | null>(null);
  const latestOnLoadMore = useRef(onLoadMore);

  useEffect(() => {
    latestOnLoadMore.current = onLoadMore;
  });

  useEffect(() => {
    if (!canLoadMore || !sentinel || !scrollRoot) return undefined;
    return observe(
      sentinel,
      (inView) => {
        if (inView) latestOnLoadMore.current?.();
      },
      { root: scrollRoot, rootMargin: PREFETCH_MARGIN },
    );
  }, [canLoadMore, itemCount, sentinel, scrollRoot]);

  return setSentinel;
};
